/**
 * A course's RUNNING ORDER — the order its seeds play in, when that is not
 * simply seed-number order (job #949, Aran's North Welsh sandbox).
 *
 * The live view `course_running_order(course_code, seed_number, position,
 * in_block)` has rows ONLY for a course with a `course_seed_weave` row: one row
 * per KEPT seed, positions contiguous 1..N. A woven course can play a block of
 * new seeds (ids 1001+) after old seed 137 and before old seed 138 while every
 * seed and LEGO id stays exactly as it is — so in a woven course the id is no
 * longer the order, and "S1001L01" > "S0138L01" as text is the wrong answer.
 * Seeds missing from a woven course's rows were DROPPED from its running order
 * (excluded, never deleted).
 *
 * PUBLISHED FRONTIER (job #621): the player reads `course_published_order`, not
 * `course_running_order`. It is the same rows cut at the course's
 * `course_seed_weave.published_through_batch` (seeds whose
 * `course_seeds.release_batch` is at or under it). A null frontier is no cut,
 * so the two views are identical for every course without one. An unpublished
 * seed is treated exactly like a dropped one: no position, so it never plays.
 *
 * THE RULE, in one place: anything that orders seeds or LEGOs, or reads "how
 * far along the course" from a LEGO id (belts, high-water marks, streamed-round
 * insertion), asks this module. A course with NO rows registers nothing, and
 * then every function here returns EXACTLY what the expression it replaced
 * returned — numeric seed sort, `a > b` on LEGO-id text, the seed number as the
 * journey position. That fallback is the hard constraint: every course without
 * a running order behaves byte-for-byte as before.
 *
 * The registry is module state, keyed by course code, filled by
 * `ensureRunningOrder` (memoised: one read per course per page load). A read
 * that fails registers nothing, so the course falls back to seed order rather
 * than half-applying a woven one. `runningOrderVersion` is a Vue ref bumped on
 * every registration, read by every lookup, so a computed belt colour that ran
 * before the map arrived recomputes when it does.
 */
import { shallowRef } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'

/** course_code → (seed_number → position). Only woven courses appear. */
const orders = new Map<string, ReadonlyMap<number, number>>()
// Sort once per registration, rather than on each listening/pronunciation
// computed read. Weak keys release the entries when an order is replaced.
const orderedEntries = new WeakMap<ReadonlyMap<number, number>, readonly [number, number][]>()
// Position → seed for course navigation. Keyed by the forward map so replacing
// or clearing an order also releases its inverse without a second lifecycle.
const seedsByPosition = new WeakMap<ReadonlyMap<number, number>, ReadonlyMap<number, number>>()
const loads = new Map<string, Promise<void>>()
const runningOrderVersion = shallowRef(0)

const PAGE = 1000

export interface RunningOrderRow {
  seed_number: number
  position: number
}

/**
 * Register a course's running order from its `course_running_order` rows.
 * Empty rows register nothing (and clear any earlier map), which is what keeps
 * an un-woven course on its old behaviour.
 */
export function registerRunningOrder(courseCode: string, rows: readonly RunningOrderRow[] | null | undefined): void {
  if (!courseCode) return
  if (!rows || rows.length === 0) {
    if (orders.delete(courseCode)) runningOrderVersion.value++
    return
  }
  const map = new Map<number, number>()
  for (const r of rows) map.set(Number(r.seed_number), Number(r.position))
  orderedEntries.set(map, [...map].sort((a, b) => a[1] - b[1]))
  const inverse = new Map<number, number>()
  for (const [seed, position] of map) {
    // Match the old scan's first match, including its rejection of NaN.
    if (!Number.isNaN(position) && !inverse.has(position)) inverse.set(position, seed)
  }
  seedsByPosition.set(map, inverse)
  orders.set(courseCode, map)
  runningOrderVersion.value++
}

/** Test seam: forget every registered order and every memoised load. */
export function resetRunningOrders(): void {
  orders.clear()
  loads.clear()
  runningOrderVersion.value++
}

function mapFor(courseCode: string | null | undefined): ReadonlyMap<number, number> | null {
  void runningOrderVersion.value // reactive dependency for computeds
  if (!courseCode) return null
  return orders.get(courseCode) ?? null
}

export function hasRunningOrder(courseCode: string | null | undefined): boolean {
  return mapFor(courseCode) !== null
}

/**
 * Load (once per course per page load) and register a course's running order.
 * Never throws: an error is logged and the course stays on seed order. A
 * failed read is not memoised, so the next caller retries.
 */
export function ensureRunningOrder(supabase: SupabaseClient | null | undefined, courseCode: string | null | undefined): Promise<void> {
  if (!supabase || !courseCode) return Promise.resolve()
  const existing = loads.get(courseCode)
  if (existing) return existing
  const p = (async () => {
    try {
      const rows: RunningOrderRow[] = []
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from('course_published_order')
          .select('seed_number, position')
          .eq('course_code', courseCode)
          .order('position', { ascending: true })
          .range(from, from + PAGE - 1)
        if (error) throw error
        const page = (data || []) as RunningOrderRow[]
        rows.push(...page)
        if (page.length < PAGE) break
      }
      registerRunningOrder(courseCode, rows)
    } catch (err) {
      loads.delete(courseCode)
      console.warn(`[runningOrder] could not read course_published_order for ${courseCode} — playing in seed order:`, (err as Error)?.message ?? err)
    }
  })()
  loads.set(courseCode, p)
  return p
}

/** Load several courses' orders (list screens showing many belts). */
export function ensureRunningOrders(supabase: SupabaseClient | null | undefined, courseCodes: Iterable<string | null | undefined>): Promise<void> {
  const unique = new Set<string>()
  for (const c of courseCodes) if (c) unique.add(c)
  return Promise.all([...unique].map((c) => ensureRunningOrder(supabase, c))).then(() => undefined)
}

/**
 * A seed's place in the learner's journey. No running order → the seed number
 * itself (the old meaning). Woven course → its position, or null when the seed
 * was dropped from the running order.
 */
export function positionForSeed(courseCode: string | null | undefined, seedNumber: number): number | null {
  const map = mapFor(courseCode)
  if (!map) return seedNumber
  return map.get(seedNumber) ?? null
}

/**
 * The seed at a journey position — the inverse of positionForSeed. No running
 * order → the position itself (seed N is at position N). Woven course → the
 * seed id there, or null past its end.
 */
export function seedAtPosition(courseCode: string | null | undefined, position: number): number | null {
  const map = mapFor(courseCode)
  if (!map) return position
  return seedsByPosition.get(map)?.get(position) ?? null
}

/**
 * The seeds a learner has REACHED, for filters that used to say
 * `seed_number < reachedSeed + 1` (listening's All tab, pronunciation).
 * No running order → null: keep the old numeric ceiling, byte for byte.
 * Woven course → every published seed whose position is at or before the
 * reached seed's (strictly before when `inclusive` is false). A woven course
 * plays old 1–137, then the new block, then old 138+ — so the numeric ceiling
 * on a block seed would include old 138+ seeds nobody was taught. A reached
 * seed with no position (dropped or not published) keeps the published seeds
 * whose id is under it.
 */
export function seedsReachedThrough(
  courseCode: string | null | undefined,
  reachedSeed: number | null | undefined,
  inclusive = true,
): number[] | null {
  const map = mapFor(courseCode)
  if (!map || reachedSeed == null) return null
  const pos = map.get(reachedSeed)
  const out: number[] = []
  for (const [seed, p] of orderedEntries.get(map)!) {
    // Entries are sorted by position. A missing seed uses a numeric-id
    // ceiling instead, which is not monotonic in a woven running order.
    if (pos !== undefined && (inclusive ? p > pos : p >= pos)) break
    const keep = pos === undefined
      ? (inclusive ? seed <= reachedSeed : seed < reachedSeed)
      : (inclusive ? p <= pos : p < pos)
    if (keep) out.push(seed)
  }
  return out
}

/**
 * Woven course → the published seeds at or before journey `position`
 * (strictly before when `inclusive` is false), in running order. No running
 * order → null. For callers that hold a journey position, not a seed id
 * (belt progress's playingSeedNumber is a position in a woven course).
 */
export function seedsThroughPosition(
  courseCode: string | null | undefined,
  position: number | null | undefined,
  inclusive = true,
): number[] | null {
  const map = mapFor(courseCode)
  if (!map || position == null) return null
  const out: number[] = []
  for (const [seed, p] of orderedEntries.get(map)!) {
    if (inclusive ? p > position : p >= position) break
    if (inclusive ? p <= position : p < position) out.push(seed)
  }
  return out
}

/** "S0045L03" → 45. Same parse as useBeltProgress.getSeedFromLegoId. */
function seedOfLegoId(legoId: string | null | undefined): number | null {
  if (!legoId) return null
  const m = legoId.match(/^S(\d{4})L/)
  return m ? parseInt(m[1], 10) : null
}

/**
 * The journey position of a LEGO's seed — what a belt threshold, a high-water
 * mark or a "how far through" fraction means. No running order → exactly
 * getSeedFromLegoId(legoId). Woven course → the seed's position (null if the
 * seed was dropped or the id does not parse).
 */
export function journeySeedFromLegoId(courseCode: string | null | undefined, legoId: string | null | undefined): number | null {
  const seed = seedOfLegoId(legoId)
  if (seed === null) return null
  return positionForSeed(courseCode, seed)
}

/**
 * Does LEGO `a` come AFTER LEGO `b` in this course? Replaces the bare text
 * comparison `a > b`. No running order → `a > b`, exactly. Woven course →
 * compares (position, lego index); if either id does not parse or its seed is
 * not in the running order, it falls back to `a > b` for that pair.
 */
export function legoIdAfter(courseCode: string | null | undefined, a: string, b: string): boolean {
  const map = mapFor(courseCode)
  if (!map) return a > b
  // Streamed round insertion repeats this comparison across existing rounds.
  // Read seed and LEGO index together instead of scanning each ID twice.
  const ma = a.match(/^S(\d{4})L(\d+)/)
  const mb = b.match(/^S(\d{4})L(\d+)/)
  if (!ma || !mb) return a > b
  const pa = map.get(parseInt(ma[1], 10))
  const pb = map.get(parseInt(mb[1], 10))
  if (pa === undefined || pb === undefined) return a > b
  const la = parseInt(ma[2], 10)
  const lb = parseInt(mb[2], 10)
  if (pa !== pb) return pa > pb
  if (la !== lb) return la > lb
  return a > b
}

/**
 * The seeds of a course in playing order. No running order → numeric sort,
 * exactly `[...seeds].sort((a, b) => a - b)`. Woven course → by position, with
 * dropped seeds (no position) left out.
 */
export function orderSeedNumbers(courseCode: string | null | undefined, seedNumbers: Iterable<number>): number[] {
  const map = mapFor(courseCode)
  const all = Array.from(seedNumbers)
  if (!map) return all.sort((a, b) => a - b)
  // Script generation sorts the course's seeds before building rounds. Resolve
  // positions once per seed rather than looking them up in every comparison.
  const positioned: { seed: number; position: number }[] = []
  for (const seed of all) {
    const position = map.get(seed)
    if (position !== undefined) positioned.push({ seed, position })
  }
  return positioned
    .sort((a, b) => a.position - b.position)
    .map(({ seed }) => seed)
}

/**
 * A woven course's new-LEGO rows in playing order — by (position, lego_index),
 * dropped seeds left out. No running order → by (seed_number, lego_index),
 * which is the order those rows always had.
 */
export function orderNewLegosByRunningOrder<T extends { seed_number: number; lego_index: number }>(
  courseCode: string | null | undefined,
  rows: readonly T[],
): T[] {
  const map = mapFor(courseCode)
  if (!map) {
    return rows
      .filter((r) => r.seed_number !== undefined)
      .sort((a, b) => (a.seed_number - b.seed_number) || (a.lego_index - b.lego_index))
  }
  // Resolve each row's position once; the sort comparator runs O(n log n)
  // times when preparing the learner's catalogue for playback.
  const positioned: { row: T; position: number }[] = []
  for (const row of rows) {
    const position = map.get(row.seed_number)
    if (position !== undefined) positioned.push({ row, position })
  }
  return positioned
    .sort((a, b) => (a.position - b.position) || (a.row.lego_index - b.row.lego_index))
    .map(({ row }) => row)
}

/**
 * Every new LEGO (is_new) of a course as (seed_number, lego_index), paginated
 * past PostgREST's max-rows. Only woven courses call this — an un-woven course
 * keeps its one-row / head-count queries.
 */
export async function fetchNewLegoKeys(
  supabase: SupabaseClient,
  courseCode: string,
): Promise<{ data: Array<{ seed_number: number; lego_index: number }>; error: unknown }> {
  const out: Array<{ seed_number: number; lego_index: number }> = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('course_legos')
      .select('seed_number, lego_index')
      .eq('course_code', courseCode)
      .eq('is_new', true)
      .order('seed_number', { ascending: true })
      .order('lego_index', { ascending: true })
      .range(from, from + PAGE - 1)
    if (error) return { data: out, error }
    const page = (data || []) as Array<{ seed_number: number; lego_index: number }>
    out.push(...page)
    if (page.length < PAGE) return { data: out, error: null }
  }
}
